import { ElementRef, useCallback, useMemo, useRef } from 'react'
import {
  Action,
  HostEvent,
  LiveboardEmbed,
  RuntimeFilterOp,
} from '@thoughtspot/visual-embed-sdk/lib/src/react'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { selectLeftPanelVisibility } from 'common/components/leftPanel/store/leftPanelSlice'
import PageContainer from 'common/components/PageContainer'
import { useAppSelector } from 'common/hooks/redux'

import LeftSection from './LeftSection'
import { EMBED_CLASS_NAME } from './styles'

export const Campaigns = () => {
  const campaignsLiveboardId = process.env.REACT_APP_CAMPAIGN_LIVEBOARD_ID_NEW
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const flags = useFlags()

  const leftPanelVisibility = useAppSelector(selectLeftPanelVisibility)

  const campaignData = useAppSelector(
    (state) => state.campaignStates?.campaignData
  )

  const campaignId = campaignData?._id
  const campaignName = campaignData?.name
  let metric = campaignData?.campaign_data?.bandit_data?.optimization_metric
  if (!metric) {
    metric =
      campaignData?.campaign_data?.bandit_data?.performance_metric?.split(
        '-'
      )[0]
  }

  // Prepend 'num_' if metric is 'clicks' or 'opens' or pluralize certain metrics
  if (metric === 'clicks' || metric === 'opens') {
    metric = `num_${metric}`
  } else if (metric === 'direct_open' || metric === 'influenced_open') {
    metric = `${metric}s`
  }

  const shouldRenderEmbed = accountId && campaignsLiveboardId

  const runtimeFilters = useMemo(() => {
    const filters = [
      {
        columnName: 'Account ID',
        operator: RuntimeFilterOp.EQ,
        values: [accountId],
      },
    ]
    return filters
  }, [accountId])

  const updateCampaignFilter = useCallback(() => {
    if (ref.current) {
      setTimeout(() => {
        if (campaignName && ref.current) {
          const campaignFilterValue = `${campaignName}_${campaignId}`
          ref.current.trigger(HostEvent.UpdateFilters, {
            filters: [
              {
                column: 'Name ID',
                oper: 'EQ',
                values: [campaignFilterValue],
              },
              {
                column: 'Metric field id',
                oper: 'EQ',
                values: [metric],
              },
            ],
          })
        }
      }, 1000)
    }
  }, [campaignId, campaignName, metric])

  const ref = useRef<ElementRef<typeof LiveboardEmbed>>(null)

  return (
    <div
      className={cx(
        'relative overflow-y-auto overflow-x-hidden flex h-full w-full',
        {
          'ml-30': leftPanelVisibility === 'hidden',
        }
      )}
    >
      {flags.showSavedReports && <LeftSection />}
      <PageContainer className="w-full flex-grow flex flex-col xl:max-w-none">
        {shouldRenderEmbed ? (
          <LiveboardEmbed
            ref={ref}
            className={EMBED_CLASS_NAME}
            liveboardId={campaignsLiveboardId}
            visibleActions={[
              Action.Download,
              Action.DownloadAsCsv,
              Action.DownloadAsPdf,
              Action.Explore,
            ]}
            runtimeFilters={runtimeFilters}
            onLiveboardRendered={() => {
              updateCampaignFilter()
            }}
            key={`${campaignsLiveboardId}-${campaignId || 'all'}`}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p>Loading campaign data...</p>
              <p className="text-sm text-gray-500 mt-2">
                Account ID: {accountId || 'Loading...'}
                <br />
                Campaign Name:{' '}
                {campaignName || 'None (will show all campaigns)'}
                <br />
                Liveboard ID: {campaignsLiveboardId || 'Not configured'}
              </p>
            </div>
          </div>
        )}
      </PageContainer>
    </div>
  )
}
